import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	CreateIssueUFOExperience,
	GlobalIssueCreateModalSubmitUFOExperience,
} from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import { transformCreateIssueUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import random from '@atlassian/jira-random';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	FormFieldValues,
	FieldConfig,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { useIssueCreateErrorAnalyticsEvent } from '../../../issue-create-analytics/src/controllers/use-error-analytics-events/index.tsx';
import { LABEL_FIELD } from '../../common/constants/graphql-field-types.tsx';
import { performPost } from './perform-post/index.tsx';
import type {
	IssueCreateResponse,
	CreateIssue,
	UseCreateIssueService,
	CreateServiceError,
} from './types.tsx';
import {
	getCreateIssueUrl,
	transformContextDataUsedForCreate,
	transformData,
	transformDataToIssueCreateFieldsUsed,
	handleCopyLinksAndAttachments,
	getUpdateFields,
} from './utils.tsx';

const getLabelsField = (formData: FormFieldValues, fieldConfig: FieldConfig) => {
	const labelsOutput: string[] = [];

	Object.keys(formData).forEach((fieldId) => {
		const fieldData = fieldConfig[fieldId];
		if (fieldData?.gqlType === LABEL_FIELD) {
			labelsOutput.push(fieldId);
		}
	});

	return labelsOutput;
};

const useCreateIssueService: UseCreateIssueService = (selectedCloudId = '') => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireErrorAnalyticsEvent } = useIssueCreateErrorAnalyticsEvent();

	const createIssue: CreateIssue = ({
		onSuccess,
		formData,
		transition: inputTransition,
		watchers,
		skipAutoWatch,
		fieldConfig,
		fieldTabs,
		hasIssueAdjustmentModules,
		context,
		createLinkedIssuePayload,
		interactedFields = {},
		shouldShowStatus = false,
	}) => {
		let transformedData = transformData(formData, fieldConfig);
		const additionalFields = context?.submitApiData?.additionalFields || {};
		const issueList = context?.submitApiData?.issueList || null;
		const transition = inputTransition ?? context?.submitApiData?.transition;
		const { isCreateLinkedIssue, sourceIssueKey } = createLinkedIssuePayload || {};

		// add copy links and attachments data to issuelinks and attachment field submit payload
		if (
			isCreateLinkedIssue &&
			sourceIssueKey &&
			fg('create_linked_issues_trigger_point_issue_view')
		) {
			transformedData = handleCopyLinksAndAttachments({
				transformedData,
				formData,
				sourceIssueKey,
			});
		}

		const updateFields = getUpdateFields(
			isCreateLinkedIssue &&
				Boolean(sourceIssueKey) &&
				fg('create_linked_issues_trigger_point_issue_view'),
		);

		const labelsFields = getLabelsField(formData, fieldConfig);

		const updateFieldsWithLabels = expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
			? [...updateFields, ...labelsFields]
			: updateFields;

		const payload = {
			fields: {
				...additionalFields,
				...omit(transformedData, updateFieldsWithLabels),
			},
			update: pick(transformedData, updateFieldsWithLabels),
			...(!isEmpty(issueList) && { issueList }),
			transition: undefined,
			watchers,
			externalToken: random().toString(),
		};

		// @ts-expect-error - TS2322 - Type '{ id: string; }' is not assignable to type 'undefined'.
		if (transition) payload.transition = transition;

		GlobalIssueCreateModalSubmitUFOExperience.start();

		const getCollapsedFields = () =>
			!hasIssueAdjustmentModules && fieldTabs
				? fieldTabs.flatMap((tab) => tab.collapsedItems || [])
				: [];

		const MAX_RETRIES = 1;

		const performPostRequestRetry = (retryCount: number): Promise<void> => {
			const analyticsEvent = createAnalyticsEvent({});

			return performPost(
				transformCreateIssueUrl({
					issueUrl: getCreateIssueUrl({
						isCreateLinkedIssue: isCreateLinkedIssue || false,
						skipAutoWatch,
					}),
					selectedCloudId,
				}),
				{
					body: JSON.stringify(payload),
				},
			)
				.then((issueCreateResponse: IssueCreateResponse) => {
					const statusWasSet = issueCreateResponse && issueCreateResponse.transition !== undefined;

					const collapsedFields = getCollapsedFields();

					let modifiedFieldConfig = fieldConfig;
					let modifiedFormData = formData;
					let modifiedInteractedFields = interactedFields;

					// If the form has a status field provided, the formConfig and formData
					// will be modified on `transformDataToIssueCreateFieldsUsed` to have the
					// hasDefaultValue and isPopulated attributes set to true for status. If the user
					// has modified the status field, the response of the API will return a transition props
					// which is used to modify the interactedFields to set hasInteractedWith to true for status.
					if (shouldShowStatus) {
						modifiedFieldConfig = {
							...fieldConfig,
							status: {
								...fieldConfig.status,
								defaultValue: true,
							},
						};
						modifiedFormData = {
							...formData,
							status: true,
						};

						if (statusWasSet) {
							modifiedInteractedFields = {
								...interactedFields,
								status: true,
							};
						}
					}

					const issueCreateFieldsUsed = transformDataToIssueCreateFieldsUsed(
						modifiedFormData,
						modifiedFieldConfig,
						modifiedInteractedFields,
						collapsedFields,
					);

					const issueCreateContextUsed = transformContextDataUsedForCreate(context);
					CreateIssueUFOExperience.success();

					GlobalIssueCreateModalSubmitUFOExperience.success();

					if (
						issueCreateResponse?.transition?.status &&
						issueCreateResponse.transition.status !== 200
					) {
						fireErrorAnalytics({
							meta: {
								id: 'useCreateIssueService',
								packageName: 'jiraIssueCreate',
								teamName: 'gryffindor',
							},
							attributes: {
								status: issueCreateResponse.transition.status,
								message: (issueCreateResponse.transition.errorCollection?.errorMessages || []).join(
									'\n',
								),
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}

					const collapsedFieldsLength = collapsedFields.length;

					onSuccess({
						issueCreateResponse,
						issueCreateFieldsUsed,
						issueCreateContextUsed,
						collapsedFieldsLength,
					});

					const attributes = {
						isRetry: retryCount < MAX_RETRIES,
						statusCode: '2xx',
					};
					fireTrackAnalyticsDeferred(analyticsEvent, 'issueCreate retry', attributes);
				})
				.catch((error: CreateServiceError) => {
					if (String(error.statusCode).startsWith('5')) {
						if (retryCount < MAX_RETRIES) {
							const attributes = {
								isRetry: true,
								statusCode: '5xx',
							};
							fireTrackAnalyticsDeferred(analyticsEvent, 'issueCreate retry', attributes);
						}
						if (retryCount > 0) {
							return performPostRequestRetry(retryCount - 1);
						}
					}
					let statusCode;
					if (error instanceof FetchError) {
						statusCode = error?.statusCode;
					}

					fireErrorAnalyticsEvent({
						meta: {
							id: 'useCreateIssueService',
							packageName: 'jiraIssueCreate',
							teamName: 'gryffindor',
						},
						error,
						statusCode,
					});

					CreateIssueUFOExperience.failure();

					GlobalIssueCreateModalSubmitUFOExperience.failure();
					throw error;
				});
		};

		return performPostRequestRetry(MAX_RETRIES);
	};
	return [createIssue];
};

export default useCreateIssueService;
